import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ColorService } from 'src/app/service/color/color.service';
import { AnalyticsService } from 'src/app/service/analytics/analytics.service';

interface HarmonyGroup {
  key: string;
  label: string;
  description: string;
  colors: string[];
}

// Live color harmony preview embedded inside a blog post. Pick a base color and see
// its analogous, complementary and triadic schemes update instantly. Reuses the
// color tool's logic (ColorService) so the wheel math matches /tools/palette.
@Component( {
  selector: 'app-harmony-preview',
  templateUrl: './harmony-preview.component.html',
  styleUrls: [ './harmony-preview.component.scss' ],
} )
export class HarmonyPreviewComponent implements OnInit {

  @Input() hex = '#00BAFF';
  // Optional heading shown above the panel.
  @Input() heading = 'Try It Yourself';
  // Optional whitelist of scheme keys to show (e.g. ['complementary','split']).
  // When null, all schemes show.
  @Input() only: string[] | null = null;
  // When true, each shown scheme (complementary / split) renders its own small
  // wheel beside the swatches, illustrating the geometry (the opposites widget).
  @Input() wheels = false;
  // When true, open on the bound [hex] (the post's color) instead of a random color.
  // Used by the opposites widget so it starts on the post's own complementary base.
  @Input() lockHex = false;
  // Emits the committed base colour so a sibling widget (the opposites color wheel)
  // can mirror it.
  @Output() colorChange = new EventEmitter<string>();

  text = '';
  groups: HarmonyGroup[] = [];

  // One engagement event on first interaction, not per keystroke.
  private tracked = false;

  constructor(
    private colorService: ColorService,
    private analyticsService: AnalyticsService,
  ) {
  }

  private trackUse(): void {
    if ( this.tracked ) {
      return;
    }
    this.tracked = true;
    this.analyticsService.logEvent( 'Blog Widget Used', { widget: 'harmony-preview' } );
  }

  ngOnInit(): void {
    // Open on the bound [hex] when locked (the post's color); otherwise start from a
    // random named color each load.
    if ( !this.lockHex ) {
      const name = this.colorService.getRandomNamedColor();
      const code = this.colorService.getHexCode( name );
      if ( code ) {
        this.hex = '#' + code.toLowerCase();
      }
    }
    this.text = this.normalizeHex( this.hex );
    this.recompute();
  }

  onText( value: string ): void {
    // Sanitise like the colour tool: hex digits only, max six, '#' kept in front.
    const raw = ( value || '' ).replace( /[^0-9a-fA-F]/g, '' ).toLowerCase().slice( 0, 6 );
    this.text = '#' + raw;
    this.recompute();
    this.trackUse();
  }

  onPicker( value: string ): void {
    this.text = ( value || '' ).toLowerCase();
    this.recompute();
    this.trackUse();
  }

  // Block non-hex keystrokes outright (control keys and shortcuts pass through).
  onKey( event: KeyboardEvent ): void {
    if ( event.ctrlKey || event.metaKey ) {
      return;
    }
    if ( event.key && event.key.length === 1 && !/[0-9a-fA-F]/.test( event.key ) ) {
      event.preventDefault();
    }
  }

  get swatch(): string {
    return this.hex;
  }

  private normalizeHex( value: string ): string {
    return '#' + ( value || '' ).trim().replace( /^#/, '' ).toLowerCase();
  }

  private recompute(): void {
    const v = ( this.text || '' ).replace( /[^0-9a-fA-F]/g, '' ).toLowerCase().slice( 0, 6 );
    // Only commit a full six-digit hex; no error state is shown.
    if ( v.length !== 6 || !this.colorService.isValidHexCode( v ) ) {
      return;
    }
    this.hex = '#' + v;

    const hsl = this.colorService.hexToHsl( v );
    const analogous = [
      '#' + this.colorService.hslToHex( ( hsl.h - 30 + 360 ) % 360, hsl.s, hsl.l ),
      '#' + v,
      '#' + this.colorService.hslToHex( ( hsl.h + 30 ) % 360, hsl.s, hsl.l ),
    ];
    // Monochrome: the same hue, a darker and a lighter step, base in the middle.
    const monochromatic = [
      '#' + this.colorService.hslToHex( hsl.h, hsl.s, Math.max( hsl.l - 16, 0 ) ),
      '#' + v,
      '#' + this.colorService.hslToHex( hsl.h, hsl.s, Math.min( hsl.l + 16, 100 ) ),
    ];
    const complementary = this.colorService.calculateComplementaryColor( v );
    const splitComplementary = this.colorService.calculateSplitComplementaryColors( v );
    const triadic = this.colorService.calculateTriadicColors( v );
    const tetradic = this.colorService.calculateTetradicColors( v );

    // Some ColorService helpers return the base entry without a leading '#',
    // so normalize every entry to a '#RRGGBB' form before rendering chips/labels.
    const up = ( list: string[] ) =>
      list.map( c => ( '#' + ( c || '' ).replace( /^#/, '' ) ).toLowerCase() );
    const all: HarmonyGroup[] = [
      {
        key: 'analogous',
        label: 'Analogous',
        description: 'Neighbouring hues. Calm, cohesive, easy on the eyes.',
        colors: up( analogous ),
      },
      {
        key: 'monochrome',
        label: 'Monochrome',
        description: 'One hue, varied in lightness. Quiet, clean, simple to balance.',
        colors: up( monochromatic ),
      },
      {
        key: 'complementary',
        label: 'Complementary',
        description: 'The opposite hue. Maximum contrast, one bold accent.',
        colors: up( complementary ),
      },
      {
        key: 'split',
        label: 'Split Complementary',
        description: 'The two hues beside the opposite. Softer tension, more range.',
        colors: up( splitComplementary ),
      },
      {
        key: 'triadic',
        label: 'Triadic',
        description: 'Three evenly spaced hues. Balanced but lively.',
        colors: up( triadic ),
      },
      {
        key: 'tetradic',
        label: 'Tetradic',
        description: 'Two complementary pairs. The richest scheme, hardest to balance.',
        colors: up( tetradic ),
      },
    ];
    this.groups = this.only ? all.filter( g => this.only!.includes( g.key ) ) : all;
    this.colorChange.emit( this.hex );
  }
}
